/**
 * CoupleDashboard - Duo feature showing combined stats and shared activity
 */
import { useState, useEffect } from "react";
import { Heart, Users, Globe, Trophy, Flame, Loader2, Lock, MapPin, Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DuoInviteCTA from "./DuoInviteCTA";
import MarkJointTripsModal from "./MarkJointTripsModal";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

export default function CoupleDashboard() {
  const { token } = useAuth();
  const { hasDuoAccess } = useEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showJointModal, setShowJointModal] = useState(false);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/premium/duo/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch couple dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data?.is_duo) {
    return <DuoInviteCTA />;
  }

  if (!hasDuoAccess) {
    return (
      <>
        <Card className="relative overflow-hidden border-pink-500/20">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-rose-500/5 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
            <Lock className="w-8 h-8 text-pink-500 mb-2" />
            <p className="text-sm font-medium">Duo Plan Required</p>
            <button onClick={() => setShowPaywall(true)} className="mt-2 px-4 py-1.5 bg-pink-500 text-white text-sm rounded-full">
              Learn more on duolyst.co.uk
            </button>
          </div>
          <CardContent className="p-6 blur-sm">
            <div className="h-40" />
          </CardContent>
        </Card>
        {showPaywall && <PaywallScreen type={PaywallType.DUO_UPSELL} isOpen={showPaywall} onClose={() => setShowPaywall(false)} />}
      </>
    );
  }

  const { stats, partner, categories, recent_activity } = data;

  return (
    <div className="space-y-4" data-testid="couple-dashboard">
      {/* Partner Header */}
      <Card className="bg-gradient-to-r from-pink-500/10 to-rose-500/10 border-pink-500/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border-2 border-card z-10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center border-2 border-card">
                <Heart className="w-5 h-5 text-pink-500" />
              </div>
            </div>
            <div>
              <p className="font-medium text-foreground">You & {partner.name}</p>
              <p className="text-xs text-muted-foreground">Travel Partners</p>
            </div>
            {stats.couple_streak > 0 && (
              <Badge className="ml-auto bg-orange-500/20 text-orange-500 border-orange-500/30">
                <Flame className="w-3 h-3 mr-1" /> {stats.couple_streak} day streak
              </Badge>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              variant="outline"
              className="border-pink-500/30 text-pink-500 hover:bg-pink-500/10"
              onClick={() => setShowJointModal(true)}
              data-testid="manage-shared-trips-btn"
            >
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Manage shared trips
              <Badge variant="secondary" className="ml-2 text-[10px]">{stats.joint_completed ?? 0}</Badge>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.total_completed}</p>
            <p className="text-xs text-muted-foreground">Adventure Combined</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.combined_countries}</p>
            <p className="text-xs text-muted-foreground">Countries Combined</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-pink-500">{stats.shared_countries}</p>
            <p className="text-xs text-muted-foreground">Visited Together</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="flex justify-center gap-2 text-sm">
              <span className="text-primary">{stats.my_completed}</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-pink-500">{stats.partner_completed}</span>
            </div>
            <p className="text-xs text-muted-foreground">Your / Theirs</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {recent_activity && recent_activity.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Trophy className="w-4 h-4 text-yellow-500" />
              Shared activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recent_activity.slice(0, 8).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Heart className="w-3 h-3 text-pink-500 flex-shrink-0" />
                  <span className="text-foreground truncate flex-1">{item.title}</span>
                  <span className="text-xs text-muted-foreground">{(item.who || "").split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-pink-500/30">
          <CardContent className="p-4 text-center text-sm text-muted-foreground">
            No shared trips yet — tap <span className="text-pink-500 font-medium">Manage shared trips</span> above to mark trips you went on together.
          </CardContent>
        </Card>
      )}

      <MarkJointTripsModal
        open={showJointModal}
        onClose={() => setShowJointModal(false)}
        onSaved={fetchDashboard}
      />
    </div>
  );
}
